import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheEntry } from '../domain/cache-entry.model';
import { HTTP_CACHE_STORE, HTTP_CACHE_TTL } from '../domain/http-cache.token';

export const HTTP_CACHE_TTL_DEFAULT = 300_000; // 5 minutes

@Injectable({
  providedIn: 'root',
})
export class HttpCacheService {
  private readonly cache = inject(HTTP_CACHE_STORE, { optional: true }) ?? new Map<string, CacheEntry>();
  private readonly ttl = inject(HTTP_CACHE_TTL, { optional: true }) ?? HTTP_CACHE_TTL_DEFAULT;

  public get(url: string): HttpResponse<unknown> | null {
    const entry = this.cache.get(url);
    if (!entry) return null;

    const isExpired = Date.now() - entry.lastRead > this.ttl;
    if (isExpired) {
      this.cache.delete(url);
      return null;
    }

    return entry.response;
  }

  public set(url: string, response: HttpResponse<unknown>): void {
    const entry: CacheEntry = { url, response, lastRead: Date.now() };
    this.cache.set(url, entry);
  }

  public clearCacheForUrl(url: string): void {
    this.cache.delete(url);
  }

  public clearAll(): void {
    this.cache.clear();
  }

  public clearMatching(pattern: string | RegExp): void {
    const regex = typeof pattern === 'string' ? new RegExp(pattern.replace(/\*/g, '.*')) : pattern;
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  public getAllKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}
